import React, { useRef, useState, useEffect } from "react";
import BaseButton from "../../components/buttons/BaseButton";
import { CanvaEditorButtonsContainer } from "./CanvaStyles";

const BASE_WIDTH = 1400;
const BASE_HEIGHT = 700;
const SELECTED_MODE_BG_COLOR = '#6fa5fc';
const SELECTED_MODE_TEXT_COLOR = '#ffffffff';

export default function CanvaEditor({
    backgroundImage,
    selectedImages,
    existingObjects,
    existingLines,
    fullWidth,
    onSave,
    onApprove,
    onOpenInstructions,
    onExportImage,
    onChange,
    onSaved,
    translate,
    activeLanguage,
    warningOptionIds,
    warningColor
}) {
    // CANVA VARS
    const canvasRef = useRef(null);
    const wrapperRef = useRef(null);
    const [background, setBackground] = useState(null);
    const [mode, setMode] = useState("image");
    const [scale, setScale] = useState(1);
    const [backgroundScale, setBackgroundScale] = useState(1);

    // OBJECTS MODE VARS
    const [objects, setObjects] = useState([]);
    const [dragging, setDragging] = useState(null);
    const [resizing, setResizing] = useState(null);
    const addedImageIdsRef = useRef(new Set());

    // DRAW MODE VARS
    const [drawing, setDrawing] = useState(false);
    const [lines, setLines] = useState([]);
    const [lineColor, setLineColor] = useState("#000000");
    const [lineWidth, setLineWidth] = useState(3);

    // TEXT MODE VARS
    const [textEditor, setTextEditor] = useState({
        visible: false,
        x: 0,
        y: 0
    });
    const [textValue, setTextValue] = useState('');
    const [editingTextId, setEditingTextId] = useState(null);

    // HISTORY VARS
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    // SELECTED OBJECTS VARS
    const [selectedObjectId, setSelectedObjectId] = useState(null);
    const [selectedLineIndex, setSelectedLineIndex] = useState(null);

    // CONTEXT MENU
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        targetId: null,
        type: null
    });

    // ################################################################################
    // ################################### USE EFFECTS ################################
    // ################################################################################

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;

        canvas.width = BASE_WIDTH * dpr;
        canvas.height = BASE_HEIGHT * dpr;

        canvas.style.width = "100%";
        canvas.style.height = "auto";

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        return () => {
            setObjects([]);
        };
    }, []);

    useEffect(() => {
        const updateScale = () => {
            if (!wrapperRef.current) return;
            const containerWidth = wrapperRef.current.offsetWidth;
            const newScale = containerWidth / BASE_WIDTH;
            setScale(newScale);
        };

        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, [fullWidth]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;

        canvas.width = BASE_WIDTH * dpr;
        canvas.height = BASE_HEIGHT * dpr;

        canvas.style.width = `${BASE_WIDTH * scale}px`;
        canvas.style.height = `${BASE_HEIGHT * scale}px`;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }, [scale]);

    useEffect(() => {
        drawCanvas();
    }, [
        objects,
        lines,
        background,
        selectedObjectId,
        selectedLineIndex,
        scale
    ]);

    useEffect(() => {
        const onKey = e => {
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (selectedObjectId) {
                    const newObjects = objects.filter(o => o.id !== selectedObjectId);
                    setObjects(newObjects);
                    setSelectedObjectId(null);
                    pushHistory(newObjects, lines);
                }
                if (selectedLineIndex !== null) {
                    const newLines = lines.filter((_, i) => i !== selectedLineIndex);
                    setLines(newLines);
                    setSelectedLineIndex(null);
                    pushHistory(objects, newLines);
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedObjectId, selectedLineIndex, objects, lines]);

    useEffect(() => {
        if (!backgroundImage?.template?.background?.file) return;

        const backgroundUrl = `${process.env.REACT_APP_API}/files/${backgroundImage?.template?.background?.file?._id}`;
        const bg = new Image();
        bg.crossOrigin = "anonymous";
        bg.src = backgroundUrl;

        bg.onload = () => {
            const canvasWidth = BASE_WIDTH;
            const canvasHeight = BASE_HEIGHT;

            const scale = Math.min(
                canvasWidth / bg.width,
                canvasHeight / bg.height
            );

            const width = bg.width * scale;
            const height = bg.height * scale;

            setBackgroundScale(scale);
            setBackground({ img: bg, width, height });
        };
    }, [backgroundImage]);

    useEffect(() => {
        if (!selectedImages || !Array.isArray(selectedImages)) return;

        selectedImages.forEach((image) => {
            if (addedImageIdsRef.current.has(image.id)) return;

            addedImageIdsRef.current.add(image.id);

            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = image.url;

            img.onload = () => {
                const newObj = {
                    id: image.id,
                    type: 'image',
                    src: image.url,
                    img,
                    image: image.image,
                    option: image.option,
                    name: image.name,
                    isAnswered: image.isAnswered,
                    w: img.width * backgroundScale,
                    h: img.height * backgroundScale,
                    x: 20,
                    y: 20
                };

                setObjects(prev => {
                    const updated = [...prev, newObj];
                    pushHistory(updated, lines);
                    return updated;
                });
            };
        });
    }, [selectedImages, backgroundScale]);

    useEffect(() => {
        if (!existingObjects || existingObjects.length === 0) return;

        Promise.all(
            existingObjects.map(obj => {
                if (obj.type === 'text') {
                    return Promise.resolve(obj);
                }

                return new Promise(resolve => {
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.src = obj.src;
                    img.onload = () => resolve({ ...obj, img });
                });
            })
        ).then(loaded => {
            setObjects(loaded);
            setHistory([{
                objects: existingObjects,
                lines: existingLines || []
            }]);
            setHistoryIndex(0);
        });
    }, [existingObjects]);

    useEffect(() => {
        if (!existingLines || existingLines.length === 0) return;
        setLines(normalizeLines(existingLines));
    }, [existingLines]);

    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenu.visible) {
                setContextMenu({ ...contextMenu, visible: false });
            }
        };
        window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, [contextMenu.visible]);

    useEffect(() => {
        if (onExportImage) {
            onExportImage.current = exportAsBlob;
        }
    }, [onExportImage]);

    useEffect(() => {
        drawCanvas();
    }, [warningOptionIds]);

    // ################################################################################
    // ################################ CONTEXT MENU ##################################
    // ################################################################################

    const onContextMenu = (e) => {
        e.preventDefault();
        const { x: mx, y: my } = getMousePos(e);

        const clickedText = [...objects].reverse().find(o => {
            if (o.type !== 'text') return false;

            const ctx = canvasRef.current.getContext("2d");
            ctx.font = `${o.fontSize}px Arial`;
            const width = ctx.measureText(o.text).width;
            const height = o.fontSize;

            return (
                mx >= o.x &&
                mx <= o.x + width &&
                my <= o.y &&
                my >= o.y - height
            );
        });

        if (clickedText) {
            setSelectedObjectId(clickedText.id);
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                targetId: clickedText.id,
                type: 'object'
            });
            return;
        }

        const clickedObj = [...objects].reverse().find(
            o =>
                o.type !== 'text' &&
                mx >= o.x &&
                mx <= o.x + o.w &&
                my >= o.y &&
                my <= o.y + o.h
        );

        if (clickedObj) {
            setSelectedObjectId(clickedObj.id);
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                targetId: clickedObj.id,
                type: 'object'
            });
        } else {
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                targetId: null,
                type: 'canvas'
            });
        }
    };

    // ################################################################################
    // ######################## FORWARD AND BACKWARD FUNCTIONS ########################
    // ################################################################################

    const sendForward = () => {
        setObjects(prev => {
            const index = prev.findIndex(o => o.id === selectedObjectId);
            if (index !== -1) {
                const newObjects = [...prev];
                const [obj] = newObjects.splice(index, 1);
                newObjects.push(obj);
                pushHistory(newObjects, lines);
                return newObjects;
            }
            return prev;
        });
    };

    const sendBackward = () => {
        setObjects(prev => {
            const index = prev.findIndex(o => o.id === selectedObjectId);
            if (index !== -1) {
                const newObjects = [...prev];
                const [obj] = newObjects.splice(index, 1);
                newObjects.unshift(obj);
                pushHistory(newObjects, lines);
                return newObjects;
            }
            return prev;
        });
    };

    // ################################################################################
    // ################################# HISTORY FUNCTIONS ############################
    // ################################################################################

    const pushHistory = (newObjects, newLines) => {
        const serializableObjects = newObjects.map(o => {
            if (o.type === 'text') {
                return {
                    id: o.id,
                    type: 'text',
                    text: o.text,
                    x: o.x,
                    y: o.y,
                    fontSize: o.fontSize,
                    color: o.color
                };
            }

            return {
                id: o.id,
                type: 'image',
                src: o.src,
                image: o.image,
                option: o.option,
                name: o.name,
                isAnswered: o.isAnswered,
                x: o.x,
                y: o.y,
                w: o.w,
                h: o.h
            };
        });

        const updatedHistory = history.slice(0, historyIndex + 1);
        updatedHistory.push({
            objects: serializableObjects,
            lines: normalizeLines(JSON.parse(JSON.stringify(newLines)))
        });
        setHistory(updatedHistory);
        setHistoryIndex(updatedHistory.length - 1);

        if (onChange) onChange();
    };

    const restoreHistory = async (state) => {
        const loadedObjects = await Promise.all(
            state.objects.map(obj => {
                if (obj.type === 'text') {
                    return obj;
                }

                return new Promise(resolve => {
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.src = obj.src;
                    img.onload = () => resolve({ ...obj, img });
                });
            })
        );
        setObjects(loadedObjects);
        setLines(normalizeLines(state.lines));
    };

    // ################################################################################
    // ################################## DRAW FUNCTIONS ##############################
    // ################################################################################

    const normalizeLines = (lines) => {
        return lines.map(line => {
            if (line.points) return line;
            return {
                color: "#000000",
                width: 3,
                points: line
            };
        });
    };

    // ################################################################################
    // ################################## TEXT FUNCTIONS ##############################
    // ################################################################################

    const addTextToCanvas = (text, clientX, clientY) => {
        const canvasRect = canvasRef.current.getBoundingClientRect();

        const x = (clientX - canvasRect.left) / scale;
        const y = (clientY - canvasRect.top) / scale;

        const textObj = {
            id: `text-${Date.now()}`,
            type: 'text',
            text,
            x,
            y,
            fontSize: 20,
            color: '#000'
        };

        setObjects(prev => {
            const updated = [...prev, textObj];
            pushHistory(updated, lines);
            return updated;
        });
    };

    const startEditingText = (textObj) => {
        const canvasRect = canvasRef.current.getBoundingClientRect();

        setEditingTextId(textObj.id);
        setTextValue(textObj.text);

        setTextEditor({
            visible: true,
            x: canvasRect.left + textObj.x * scale,
            y: canvasRect.top + textObj.y * scale
        });
    };

    const updateTextObject = (text) => {
        setObjects(prev => {
            const updated = prev.map(o =>
                o.id === editingTextId
                    ? { ...o, text }
                    : o
            );

            pushHistory(updated, lines);
            return updated;
        });

        setEditingTextId(null);
    };

    // ################################################################################
    // ############################# COORDINATES FUNCTIONS ############################
    // ################################################################################

    const distancePointToSegment = (p, v, w) => {
        const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
        if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
        let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
        t = Math.max(0, Math.min(1, t));
        const proj = { x: v.x + t * (w.x - v.x), y: v.y + t * (w.y - v.y) };
        return Math.hypot(p.x - proj.x, p.y - proj.y);
    };

    const getMousePos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        return {
            x: (e.clientX - rect.left) / scale,
            y: (e.clientY - rect.top) / scale
        };
    };

    // ################################################################################
    // ################################# CANVAS RESIZE ################################
    // ################################################################################

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, BASE_WIDTH, BASE_HEIGHT);

        if (background) {
            const x = (BASE_WIDTH - background.width) / 2;
            const y = (BASE_HEIGHT - background.height) / 2;
            ctx.drawImage(background.img, x, y, background.width, background.height);
        }

        objects.forEach(obj => {
            const ctx = canvasRef.current.getContext("2d");

            if (obj.type === 'text') {
                ctx.fillStyle = obj.color;
                ctx.font = `${obj.fontSize}px Arial`;
                ctx.fillText(obj.text, obj.x, obj.y);
            } else {
                ctx.drawImage(obj.img, obj.x, obj.y, obj.w, obj.h);
            }

            if (
                warningOptionIds?.length > 0 &&
                obj.type === 'image' &&
                warningOptionIds.includes(String(obj.option))
            ) {
                ctx.strokeStyle = warningColor || '#ff0004ff';
                ctx.lineWidth = 4;
                ctx.strokeRect(
                    obj.x - 2,
                    obj.y - 2,
                    obj.w + 4,
                    obj.h + 4
                );
            } else {
                if (obj.id === selectedObjectId) {
                    ctx.strokeStyle = '#1890ff';
                    ctx.lineWidth = 2;
                    if (obj.type === 'text') {
                        const width = ctx.measureText(obj.text).width;
                        ctx.strokeRect(obj.x - 2, obj.y - obj.fontSize - 2, width + 4, obj.fontSize + 4);
                    } else {
                        ctx.strokeRect(obj.x - 2, obj.y - 2, obj.w + 4, obj.h + 4);
                    }

                    if (obj.type === 'image' && obj.name[activeLanguage]) {
                        const labelText = obj.name[activeLanguage] ? obj.name[activeLanguage] : '';
                        ctx.font = 'bold 16px Arial';
                        const labelWidth = ctx.measureText(labelText).width;
                        const labelHeight = 22;
                        const labelX = obj.x + obj.w - labelWidth - 3;
                        const labelY = obj.y - 8;

                        ctx.fillStyle = '#1890ff';
                        ctx.fillRect(labelX - 6, labelY - labelHeight + 6, labelWidth + 12, labelHeight);

                        ctx.fillStyle = '#ffffff';
                        ctx.font = 'bold 16px Arial';
                        ctx.fillText(labelText, labelX, labelY);
                    }
                }
            }
        });

        lines.forEach((line, index) => {
            if (!line.points?.length) return;

            ctx.strokeStyle = index === selectedLineIndex
                ? '#ff4d4f'
                : line.color;

            ctx.lineWidth = index === selectedLineIndex
                ? line.width + 2
                : line.width;

            ctx.beginPath();
            line.points.forEach((p, i) => {
                if (i === 0) ctx.moveTo(p.x, p.y);
                else ctx.lineTo(p.x, p.y);
            });
            ctx.stroke();
        });
    };

    // ################################################################################
    // ################################## MOUSE EVENTS ################################
    // ################################################################################

    const onMouseDown = e => {
        const rect = canvasRef.current.getBoundingClientRect();
        const { x: mx, y: my } = getMousePos(e);

        setSelectedObjectId(null);
        setSelectedLineIndex(null);

        const clickedText = [...objects].reverse().find(o => {
            if (o.type !== 'text') return false;

            const ctx = canvasRef.current.getContext("2d");
            ctx.font = `${o.fontSize}px Arial`;
            const width = ctx.measureText(o.text).width;
            const height = o.fontSize;

            return (
                mx >= o.x &&
                mx <= o.x + width &&
                my <= o.y &&
                my >= o.y - height
            );
        });

        if (clickedText) {
            setSelectedObjectId(clickedText.id);
            setDragging({
                id: clickedText.id,
                offsetX: mx - clickedText.x,
                offsetY: my - clickedText.y
            });
            return;
        }

        if (mode === "image") {
            const clickedObj = [...objects].reverse().find(
                o => mx >= o.x && mx <= o.x + o.w && my >= o.y && my <= o.y + o.h
            );

            if (clickedObj) {
                setSelectedObjectId(clickedObj.id);

                //! ESTE BLOCO SERVE PARA REDIMENSIONAR OS OBJETOS
                // const handleSize = 10;
                // const isHandle =
                //     mx >= clickedObj.x + clickedObj.w - handleSize &&
                //     my >= clickedObj.y + clickedObj.h - handleSize;

                // if (isHandle) {
                //     setResizing({
                //         id: clickedObj.id,
                //         startX: mx,
                //         startY: my,
                //         startW: clickedObj.w,
                //         startH: clickedObj.h
                //     });
                // } else {
                //     setDragging({
                //         id: clickedObj.id,
                //         offsetX: mx - clickedObj.x,
                //         offsetY: my - clickedObj.y
                //     });
                // }

                setDragging({
                    id: clickedObj.id,
                    offsetX: mx - clickedObj.x,
                    offsetY: my - clickedObj.y
                });

                return;
            }

            const lineIndex = lines.findIndex(line =>
                line.points.some((p, i) =>
                    i > 0 &&
                    distancePointToSegment(
                        { x: mx, y: my },
                        line.points[i - 1],
                        p
                    ) < line.width + 3
                )
            );

            if (lineIndex !== -1) {
                setSelectedLineIndex(lineIndex);
            }

            return;
        }

        if (mode === "draw") {
            const rect = canvasRef.current.getBoundingClientRect();
            const { x: mx, y: my } = getMousePos(e);

            const lineIndex = lines.findIndex(line =>
                line.points.some((p, i) =>
                    i > 0 &&
                    distancePointToSegment(
                        { x: mx, y: my },
                        line.points[i - 1],
                        p
                    ) < line.width + 3
                )
            );

            if (lineIndex !== -1) {
                setSelectedLineIndex(lineIndex);
            } else {
                setDrawing(true);
                setLines(prev => [
                    ...prev,
                    {
                        color: lineColor,
                        width: lineWidth,
                        points: [{ x: mx, y: my }]
                    }
                ]);
            }
        }
    };

    const onMouseMove = e => {
        const rect = canvasRef.current.getBoundingClientRect();
        const { x: mx, y: my } = getMousePos(e);
        updateCursor(mx, my);

        if (mode === "image") {
            if (dragging) {
                setObjects(objects.map(o =>
                    o.id === dragging.id
                        ? { ...o, x: mx - dragging.offsetX, y: my - dragging.offsetY }
                        : o
                ));
            }

            if (resizing) {
                setObjects(objects.map(o =>
                    o.id === resizing.id
                        ? {
                            ...o,
                            w: Math.max(10, resizing.startW + (mx - resizing.startX)),
                            h: Math.max(10, resizing.startH + (my - resizing.startY))
                        }
                        : o
                ));
            }
        }

        if (mode === "draw" && drawing) {
            setLines(prev => {
                const newLines = [...prev];
                newLines[newLines.length - 1].points.push({ x: mx, y: my });
                return newLines;
            });
        }
    };

    const updateCursor = (mx, my) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const overResize = objects.find(o => o.type === 'text' && isOverTextResizeHandle(o, mx, my));
        if (overResize) {
            canvas.style.cursor = 'ns-resize';
            return;
        }

        const overObject = [...objects].reverse().find(o => {
            if (o.type === 'text') {
                const ctx = canvas.getContext("2d");
                ctx.font = `${o.fontSize}px Arial`;
                const width = ctx.measureText(o.text).width;
                return mx >= o.x && mx <= o.x + width && my <= o.y && my >= o.y - o.fontSize;
            } else {
                return mx >= o.x && mx <= o.x + o.w && my >= o.y && my <= o.y + o.h;
            }
        });

        if (overObject) {
            canvas.style.cursor = 'grab';
        } else {
            canvas.style.cursor = mode === 'draw' ? 'crosshair' : 'default';
        }
    };

    const isOverTextResizeHandle = (obj, mx, my) => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.font = `${obj.fontSize}px Arial`;
        const textWidth = ctx.measureText(obj.text).width;
        const textHeight = obj.fontSize;

        const handleSize = 10;
        const handleX = obj.x + textWidth - handleSize / 2;
        const handleY = obj.y - textHeight + textHeight - handleSize / 2;

        return mx >= handleX && mx <= handleX + handleSize &&
            my >= handleY && my <= handleY + handleSize;
    }

    const onMouseUp = () => {
        if (dragging || resizing || drawing) {
            pushHistory(objects, lines);
        }
        setDragging(null);
        setResizing(null);
        setDrawing(false);
    };

    const onDoubleClick = (e) => {
        const { x: mx, y: my } = getMousePos(e);

        const clickedText = [...objects].reverse().find(o => {
            if (o.type !== 'text') return false;

            const ctx = canvasRef.current.getContext("2d");
            ctx.font = `${o.fontSize}px Arial`;
            const width = ctx.measureText(o.text).width;
            const height = o.fontSize;

            return (
                mx >= o.x &&
                mx <= o.x + width &&
                my <= o.y &&
                my >= o.y - height
            );
        });

        if (clickedText) {
            startEditingText(clickedText);
        }
    };

    // ################################################################################
    // ################################# GET CANVA IMAGE ##############################
    // ################################################################################

    const exportAsImage = () => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const dataUrl = canvas.toDataURL("image/png");
        return dataUrl;
    };

    const exportAsBlob = () => {
        return new Promise(resolve => {
            const canvas = canvasRef.current;
            if (!canvas) resolve(null);

            canvas.toBlob(blob => {
                resolve(blob);
            }, "image/png");
        });
    };

    const downloadImage = () => {
        const dataUrl = exportAsImage();
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${backgroundImage?.name || 'canva'}.png`;
        link.click();
    };

    // ################################################################################
    // ################################# SUBMIT CANVA #################################
    // ################################################################################

    const save = () => {
        clearSelection();

        const payload = {
            objects: objects.map(o => {
                if (o.type === 'text') {
                    return {
                        id: o.id,
                        type: 'text',
                        text: o.text,
                        x: o.x,
                        y: o.y,
                        fontSize: o.fontSize,
                        color: o.color
                    };
                }

                return {
                    id: o.id,
                    type: 'image',
                    src: o.src,
                    image: o.image,
                    option: o.option,
                    name: o.name,
                    x: o.x,
                    y: o.y,
                    w: o.w,
                    h: o.h
                };
            }),
            lines
        };

        onSave(payload);

        if (onSaved) onSaved();
    };

    const approve = () => {
        clearSelection();

        const payload = {
            objects: objects.map(o => {
                if (o.type === 'text') {
                    return {
                        id: o.id,
                        type: 'text',
                        text: o.text,
                        x: o.x,
                        y: o.y,
                        fontSize: o.fontSize,
                        color: o.color
                    };
                }

                return {
                    id: o.id,
                    type: 'image',
                    src: o.src,
                    image: o.image,
                    option: o.option,
                    name: o.name,
                    x: o.x,
                    y: o.y,
                    w: o.w,
                    h: o.h
                };
            }),
            lines
        };

        onApprove(payload);
    };

    const clearSelection = () => {
        setSelectedObjectId(null);
        setSelectedLineIndex(null);
    };

    // ################################################################################
    // ############################ BUTTONS CLICK ACTIONS #############################
    // ################################################################################

    const toggleCanvaMode = (mode) => {
        setMode(mode);
    };

    const undo = () => {
        if (historyIndex > 0) {
            const prevState = history[historyIndex - 1];
            restoreHistory(prevState);
            setHistoryIndex(historyIndex - 1);
        }
    };

    const redo = () => {
        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            restoreHistory(nextState);
            setHistoryIndex(historyIndex + 1);
        }
    };

    const setDrawLineWidth = (width) => {
        setLineWidth(width);
    };

    const setDrawLineColor = (color) => {
        setLineColor(color);
    };

    const removeImage = () => {
        const newObjects = objects.filter(o => o.id !== selectedObjectId);
        setObjects(newObjects);
        setSelectedObjectId(null);
        pushHistory(newObjects, lines);
    };

    const removeLine = () => {
        const newLines = lines.filter((_, i) => i !== selectedLineIndex);
        setLines(newLines);
        setSelectedLineIndex(null);
        pushHistory(objects, newLines);
    };

    const getSelectedObject = () => {
        if (!selectedObjectId) {
            return 'Nenhum objeto selecionado';
        }

        const selected = objects.find(o => o.id === selectedObjectId);
        return selected?.name[activeLanguage] ? selected?.name[activeLanguage] : '';
    };

    // ################################################################################
    // #################################### HTML ######################################
    // ################################################################################

    return (
        <div
            ref={wrapperRef}
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column'
            }}
        >
            <div style={{
                display: 'flex',
                gap: '10px',
                padding: '30px 10px',
                marginBottom: '12px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                fontSize: '14px',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '30px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <strong>{translate("SELECTED_OBJECT")}:</strong> {getSelectedObject()}
                </div>
                {selectedObjectId && (
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <BaseButton
                            type="default"
                            icon="up"
                            text={translate("SEND_FORWARD")}
                            onClick={sendForward}
                        />
                        <BaseButton
                            type="default"
                            icon="down"
                            text={translate("SEND_BACKWARD")}
                            onClick={sendBackward}
                        />
                        <BaseButton
                            type="danger"
                            icon="delete"
                            text={translate("DELETE")}
                            onClick={removeImage}
                        />
                    </div>
                )}
            </div>
            <canvas
                ref={canvasRef}
                style={{
                    border: "1px solid #aaa",
                    cursor: mode === "draw" ? "crosshair" : "grab",
                    display: "block",
                    width: '100%'
                }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onContextMenu={onContextMenu}
                onDoubleClick={onDoubleClick}
            />
            {textEditor.visible && (
                <input
                    autoFocus
                    type="text"
                    value={textValue}
                    onChange={e => setTextValue(e.target.value)}
                    onBlur={() => {
                        if (textValue.trim()) {
                            if (editingTextId) {
                                updateTextObject(textValue);
                            } else {
                                addTextToCanvas(textValue, textEditor.x, textEditor.y);
                            }
                        }

                        setTextEditor({ visible: false, x: 0, y: 0 });
                        setTextValue('');
                        setEditingTextId(null);
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.target.blur();
                        }

                        if (e.key === 'Escape') {
                            setTextEditor({ visible: false, x: 0, y: 0 });
                            setTextValue('');
                            setEditingTextId(null);
                        }
                    }}
                    style={{
                        position: 'fixed',
                        top: textEditor.y,
                        left: textEditor.x,
                        zIndex: 10000,
                        fontSize: 16,
                        padding: '4px 8px',
                        borderRadius: 4,
                        border: '1px solid #1890ff',
                        outline: 'none'
                    }}
                />
            )}

            <CanvaEditorButtonsContainer>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <BaseButton
                        type="default"
                        icon="undo"
                        onClick={undo}
                        disabled={historyIndex <= 0}
                    />
                    <BaseButton
                        type="default"
                        icon="redo"
                        onClick={redo}
                        disabled={historyIndex >= history.length - 1}
                    />

                    {mode === "draw" && (
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            borderRadius: 6
                        }}>
                            <input
                                type="range"
                                min={1}
                                max={10}
                                value={lineWidth}
                                onChange={e => setDrawLineWidth(Number(e?.target?.value))}
                            />
                            <input
                                type="color"
                                value={lineColor}
                                onChange={e => setDrawLineColor(e?.target?.value)}
                            />
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {mode === "image" && selectedObjectId && <BaseButton
                        type="danger"
                        icon="delete"
                        text={translate("DELETE")}
                        onClick={removeImage}
                    />}

                    {mode === "draw" && selectedLineIndex !== null && <BaseButton
                        type="danger"
                        icon="delete"
                        text={translate("DELETE")}
                        onClick={removeLine}
                    />}

                    {/* <BaseButton
                        style={{ background: `${mode === 'draw' ? SELECTED_MODE_BG_COLOR : 'inherit'}`, color: `${mode === 'draw' ? SELECTED_MODE_TEXT_COLOR : 'inherit'}` }}
                        type="default"
                        icon="edit"
                        text="Desenho"
                        onClick={() => toggleCanvaMode("draw")}
                    /> */}

                    {/* <BaseButton
                        style={{ background: `${mode === 'image' ? SELECTED_MODE_BG_COLOR : 'inherit'}`, color: `${mode === 'image' ? SELECTED_MODE_TEXT_COLOR : 'inherit'}` }}
                        type="default"
                        icon="eye"
                        text="Objetos"
                        onClick={() => toggleCanvaMode("image")}
                    /> */}

                    {/* <BaseButton
                        style={{ backgroundColor: '#51a7fc', color: 'white' }}
                        type="primary"
                        icon="info"
                        text=""
                        onClick={openInstructions}
                    /> */}

                    <BaseButton
                        type="default"
                        icon="save"
                        text={translate("SAVE")}
                        onClick={save}
                    />

                    <BaseButton
                        type="primary"
                        icon="check"
                        text={translate("FINISH_CANVA")}
                        onClick={approve}
                    />
                </div>
            </CanvaEditorButtonsContainer>

            {contextMenu.visible && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    position: "fixed",
                    top: contextMenu.y,
                    left: contextMenu.x,
                    background: "#fff",
                    border: "1px solid #ccc",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    borderRadius: 4,
                    zIndex: 9999,
                    padding: 4
                }}>
                    {contextMenu.type === 'object' && (
                        <>
                            <BaseButton
                                type="default"
                                icon="up"
                                text={translate("SEND_FORWARD")}
                                onClick={sendForward}
                            />
                            <BaseButton
                                type="default"
                                icon="down"
                                text={translate("SEND_BACKWARD")}
                                onClick={sendBackward}
                            />
                            <BaseButton
                                type="danger"
                                icon="delete"
                                text={translate("DELETE")}
                                onClick={removeImage}
                            />
                        </>
                    )}

                    {contextMenu.type === 'canvas' && (
                        <>
                            <BaseButton
                                type="default"
                                icon="edit"
                                text={translate("WRITE")}
                                onClick={() => {
                                    setTextEditor({
                                        visible: true,
                                        x: contextMenu.x,
                                        y: contextMenu.y
                                    });
                                    setTextValue('');
                                    setContextMenu({ ...contextMenu, visible: false });
                                }}
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
